import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { Modal } from '@/components/ui/Modal';
import { inputClass, labelClass } from '@/components/ui/inputStyles';
import { MicroPromotionEntryDetailModal } from '@/components/MicroPromotionEntryDetailModal';
import { cn } from '@/lib/cn';

const PER_PAGE = 20;

type LogRow = {
  id: number;
  area_id: number;
  area_name?: string;
  area_public_id?: string;
  micro_entry_id: number;
  info_type: string;
  info_type_label?: string;
  entry_title?: string;
  status: string;
  status_label?: string;
  error_message?: string | null;
  content_preview?: string | null;
  created_at: string;
};

type ListResp = {
  ok: boolean;
  items: LogRow[];
  total: number;
  page: number;
  per_page: number;
};

type AreaOption = {
  id: number;
  public_id: string;
  name: string;
  enabled: boolean;
};

export function MicroPromotionWeworkPushLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const presetAreaId = Math.max(0, Number(searchParams.get('area_id') || searchParams.get('active_id') || 0) || 0);
  const [items, setItems] = useState<LogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [areas, setAreas] = useState<AreaOption[]>([]);
  const [areaDraft, setAreaDraft] = useState(presetAreaId);
  const [areaApplied, setAreaApplied] = useState(presetAreaId);
  const [qDraft, setQDraft] = useState('');
  const [qApplied, setQApplied] = useState('');
  const [infoTypeDraft, setInfoTypeDraft] = useState('');
  const [infoTypeApplied, setInfoTypeApplied] = useState('');
  const [statusDraft, setStatusDraft] = useState('');
  const [statusApplied, setStatusApplied] = useState('');
  const [startDraft, setStartDraft] = useState('');
  const [endDraft, setEndDraft] = useState('');
  const [startApplied, setStartApplied] = useState('');
  const [endApplied, setEndApplied] = useState('');
  const [detailLog, setDetailLog] = useState<LogRow | null>(null);
  const [entryDetailId, setEntryDetailId] = useState(0);
  const [entryDetailOpen, setEntryDetailOpen] = useState(false);
  const [busyId, setBusyId] = useState(0);

  const syncAreaToUrl = useCallback(
    (areaId: number) => {
      const sp = new URLSearchParams(searchParams);
      if (areaId > 0) {
        sp.set('area_id', String(areaId));
      } else {
        sp.delete('area_id');
        sp.delete('active_id');
      }
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const loadAreas = useCallback(async () => {
    const r = await apiFetch<{ ok: boolean; items?: AreaOption[] }>('list_promotion_areas', {
      method: 'GET',
      query: { page: 1, per_page: 200 },
    });
    if (r.ok && Array.isArray(r.items)) {
      setAreas(r.items);
    } else {
      setAreas([]);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setMsg('');
    try {
      const r = await apiFetch<ListResp>('list_micro_promotion_wework_push_logs', {
        method: 'GET',
        query: {
          page,
          per_page: PER_PAGE,
          ...(areaApplied > 0 ? { area_id: areaApplied } : {}),
          ...(qApplied.trim() ? { q: qApplied.trim() } : {}),
          ...(infoTypeApplied ? { info_type: infoTypeApplied } : {}),
          ...(statusApplied ? { status: statusApplied } : {}),
          ...(startApplied ? { start_date: startApplied } : {}),
          ...(endApplied ? { end_date: endApplied } : {}),
        },
      });
      if (r.ok) {
        setItems(r.items ?? []);
        setTotal(r.total ?? 0);
      } else {
        setItems([]);
        setTotal(0);
        setMsg('加载失败');
      }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '加载失败');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, areaApplied, qApplied, infoTypeApplied, statusApplied, startApplied, endApplied]);

  useEffect(() => {
    void loadAreas();
  }, [loadAreas]);

  useEffect(() => {
    void loadLogs();
  }, [loadLogs]);

  function applyAreaFilter(id: number) {
    setAreaDraft(id);
    setAreaApplied(id);
    setPage(1);
    syncAreaToUrl(id);
  }

  function runSearch() {
    setQApplied(qDraft);
    setInfoTypeApplied(infoTypeDraft);
    setStatusApplied(statusDraft);
    setStartApplied(startDraft);
    setEndApplied(endDraft);
    setAreaApplied(areaDraft);
    syncAreaToUrl(areaDraft);
    setPage(1);
  }

  function resetSearch() {
    setQDraft('');
    setQApplied('');
    setAreaDraft(0);
    setAreaApplied(0);
    setInfoTypeDraft('');
    setInfoTypeApplied('');
    setStatusDraft('');
    setStatusApplied('');
    setStartDraft('');
    setEndDraft('');
    setStartApplied('');
    setEndApplied('');
    setPage(1);
    syncAreaToUrl(0);
  }

  async function deleteLog(id: number) {
    if (
      !window.confirm(
        '确定删除这条企微推送记录？\n\n删除后该次推送不计入已完成次数，对应条目在满足时段、频次与间隔时可再次推送。',
      )
    ) {
      return;
    }
    setBusyId(id);
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('delete_micro_promotion_wework_push_log', {
        method: 'POST',
        body: JSON.stringify({ log_id: id }),
      });
      if (!r.ok) {
        setMsg('删除失败');
        return;
      }
      if (detailLog?.id === id) setDetailLog(null);
      await loadLogs();
    } finally {
      setBusyId(0);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const filteredArea = areas.find((a) => a.id === areaApplied);

  return (
    <div className="space-y-6">
      <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-slate-500">
        <Link to="/promotion-areas" className="rounded-md hover:bg-slate-100 hover:text-teal-800">
          微推广管理
        </Link>
        <span className="text-slate-300" aria-hidden>
          /
        </span>
        <span className="font-medium text-slate-800">企微推送日志</span>
      </nav>

      <PageHeader
        title={filteredArea ? `微推广 · ${filteredArea.name} · 企微推送日志` : '微推广 · 企微推送日志'}
        description="记录向企业微信内部群机器人 Webhook 推送的执行结果。定时任务每次最多推送 1 条，并按「最小推送间隔」控制下一条。"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" onClick={() => void loadLogs()} disabled={loading}>
              刷新
            </Button>
            <Link
              to={
                areaApplied > 0
                  ? `/promotion-areas/${areaApplied}?view=config`
                  : '/promotion-areas'
              }
            >
              <Button type="button" variant="secondary">
                返回推广区域
              </Button>
            </Link>
          </div>
        }
      />

      {msg ? (
        <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">{msg}</p>
      ) : null}

      <Card title="筛选">
        <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
          <label className={`${labelClass} min-w-[12rem] sm:w-52`}>
            <span>推广区域</span>
            <select
              className={inputClass}
              value={areaDraft}
              onChange={(e) => applyAreaFilter(Number(e.target.value) || 0)}
            >
              <option value={0}>全部区域</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                  {!a.enabled ? '（已停用）' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className={`${labelClass} min-w-[12rem] flex-[2]`}>
            <span>关键词</span>
            <input
              className={inputClass}
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runSearch();
              }}
              placeholder="正文、条目 ID、区域名、标题"
            />
          </label>
          <label className={`${labelClass} w-full sm:w-36`}>
            <span>信息类型</span>
            <select className={inputClass} value={infoTypeDraft} onChange={(e) => setInfoTypeDraft(e.target.value)}>
              <option value="">全部</option>
              <option value="graphic">图文信息</option>
              <option value="recruitment">招聘信息</option>
            </select>
          </label>
          <label className={`${labelClass} w-full sm:w-32`}>
            <span>状态</span>
            <select className={inputClass} value={statusDraft} onChange={(e) => setStatusDraft(e.target.value)}>
              <option value="">全部</option>
              <option value="ok">成功</option>
              <option value="failed">失败</option>
            </select>
          </label>
          <label className={`${labelClass} w-full sm:w-40`}>
            <span>开始日期</span>
            <input type="date" className={inputClass} value={startDraft} onChange={(e) => setStartDraft(e.target.value)} />
          </label>
          <label className={`${labelClass} w-full sm:w-40`}>
            <span>结束日期</span>
            <input type="date" className={inputClass} value={endDraft} onChange={(e) => setEndDraft(e.target.value)} />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="primary" onClick={runSearch} disabled={loading}>
              查询
            </Button>
            <Button type="button" variant="secondary" onClick={resetSearch} disabled={loading}>
              重置
            </Button>
          </div>
        </div>
      </Card>

      <Card title="推送记录">
        {loading ? (
          <Spinner label="加载中…" />
        ) : items.length === 0 ? (
          <EmptyState>暂无企微推送记录。启用 Webhook 推送并成功发送到企微群后，将在此显示。</EmptyState>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
              <table className="min-w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600">
                    <th className="whitespace-nowrap px-3 py-3">时间</th>
                    <th className="min-w-[100px] px-3 py-3">推广区域</th>
                    <th className="whitespace-nowrap px-3 py-3">类型</th>
                    <th className="min-w-[140px] px-3 py-3">推广条目</th>
                    <th className="whitespace-nowrap px-3 py-3">状态</th>
                    <th className="min-w-[200px] px-3 py-3">推送摘要</th>
                    <th className="sticky right-0 min-w-[140px] border-l border-slate-100 bg-slate-50 px-2 py-3 text-right">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((row) => (
                    <tr key={row.id} className="bg-white hover:bg-slate-50/80">
                      <td className="whitespace-nowrap px-3 py-3 text-xs text-slate-600">{row.created_at || '—'}</td>
                      <td className="px-3 py-3">
                        <p className="font-medium text-slate-800">{row.area_name || `区域 #${row.area_id}`}</p>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3">
                        <span
                          className={cn(
                            'inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1',
                            row.info_type === 'recruitment'
                              ? 'bg-violet-50 text-violet-900 ring-violet-200/80'
                              : 'bg-sky-50 text-sky-900 ring-sky-200/80',
                          )}
                        >
                          {row.info_type_label || row.info_type}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <p className="font-medium text-slate-800">{row.entry_title || `条目 #${row.micro_entry_id}`}</p>
                        <p className="mt-0.5 font-mono text-[11px] text-slate-400">#{row.micro_entry_id}</p>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3">
                        <span
                          className={cn(
                            'inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1',
                            row.status === 'ok'
                              ? 'bg-emerald-50 text-emerald-800 ring-emerald-200/80'
                              : 'bg-rose-50 text-rose-800 ring-rose-200/80',
                          )}
                        >
                          {row.status_label || row.status}
                        </span>
                        {row.status !== 'ok' && row.error_message ? (
                          <p className="mt-1 max-w-[10rem] truncate text-[11px] text-rose-600" title={row.error_message}>
                            {row.error_message}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-3 py-3">
                        <p className="line-clamp-2 whitespace-pre-wrap text-[12px] leading-snug text-slate-600">
                          {row.content_preview || '—'}
                        </p>
                      </td>
                      <td className="sticky right-0 border-l border-slate-100 bg-white px-2 py-3 text-right">
                        <div className="flex flex-wrap justify-end gap-1">
                          <Button type="button" variant="secondary" className="!px-2 !py-1 text-xs" onClick={() => setDetailLog(row)}>
                            详情
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            className="!px-2 !py-1 text-xs"
                            onClick={() => {
                              setEntryDetailId(row.micro_entry_id);
                              setEntryDetailOpen(true);
                            }}
                          >
                            条目
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            className="!px-2 !py-1 text-xs text-rose-700 hover:border-rose-300"
                            disabled={busyId === row.id}
                            onClick={() => void deleteLog(row.id)}
                          >
                            {busyId === row.id ? '…' : '删除'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar page={page} totalPages={totalPages} total={total} onPageChange={setPage} className="mt-4" />
          </>
        )}
      </Card>

      <Modal open={detailLog !== null} onClose={() => setDetailLog(null)} title="推送详情" wide>
        {detailLog ? (
          <div className="space-y-4 text-sm">
            <dl className="grid gap-2 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-slate-500">时间</dt>
                <dd className="font-medium text-slate-800">{detailLog.created_at}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">状态</dt>
                <dd className="font-medium text-slate-800">{detailLog.status_label || detailLog.status}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">推广区域</dt>
                <dd className="font-medium text-slate-800">{detailLog.area_name}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">条目</dt>
                <dd className="font-medium text-slate-800">
                  {detailLog.entry_title} (#{detailLog.micro_entry_id})
                </dd>
              </div>
            </dl>
            {detailLog.error_message ? (
              <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-rose-900">{detailLog.error_message}</p>
            ) : null}
            <div>
              <p className="mb-2 text-xs font-semibold text-slate-600">推送正文</p>
              <pre className="max-h-[360px] overflow-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-800">
                {detailLog.content_preview || '（无正文记录）'}
              </pre>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
              <Button type="button" variant="secondary" onClick={() => void deleteLog(detailLog.id)} disabled={busyId === detailLog.id}>
                删除记录
              </Button>
              <Button type="button" variant="primary" onClick={() => setDetailLog(null)}>
                关闭
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      <MicroPromotionEntryDetailModal
        entryId={entryDetailId}
        open={entryDetailOpen}
        onClose={() => {
          setEntryDetailOpen(false);
          setEntryDetailId(0);
        }}
      />
    </div>
  );
}
